import { AfterViewInit, Directive, ElementRef, inject, input, OnDestroy, output, Renderer2 } from '@angular/core';

type Config =
  | string // shorthand: property name (e.g. "disabled" or "readOnly")
  | {
    /** Host property to set (e.g. "disabled", "readOnly"). Default: "disabled". */
    prop?: string;
    /** Host attribute fallback to set if the property doesn't exist (e.g. "aria-readonly"). */
    attr?: string;
    /** Also reflect to a standard ARIA attr when known (aria-disabled / aria-readonly). Default: true. */
    reflectAria?: boolean;
    /** Invert the value. Default: false. */
    invert?: boolean;
  };

@Directive({
  selector: '[appFieldsetSupport]',
  standalone: true
})
export class FieldsetSupportDirective implements AfterViewInit, OnDestroy {
  private hostElement = (inject(ElementRef<HTMLElement>) as ElementRef<HTMLElement>).nativeElement;
  private renderer = inject(Renderer2);
  private fieldsetElement: HTMLFieldSetElement | null = null;
  private fieldsetObserver: MutationObserver | null = null;

  public config = input<Config>('disabled', { alias: 'appFieldsetSupport' });
  public inheritedDisabledChange = output<boolean>();

  ngAfterViewInit(): void {
    // Find nearest ancestor fieldset once (DOM structure assumed stable)
    this.fieldsetElement = this.hostElement.closest('fieldset');

    if (this.fieldsetElement) {
      this.syncFromFieldset();

      // Observe only the disabled attribute
      this.fieldsetObserver = new MutationObserver(muts => {
        if (muts.some((m: MutationRecord) => m.attributeName === 'disabled')) {
          this.syncFromFieldset();
        }
      });
      this.fieldsetObserver.observe(this.fieldsetElement, { attributes: true, attributeFilter: ['disabled'], });
    }
  }

  ngOnDestroy(): void {
    this.fieldsetObserver?.disconnect();
  }

  private syncFromFieldset(): void {
    const disabled = !!this.fieldsetElement.disabled;
    const { prop, attr, reflectAria, invert } = this.normalizeConfig(this.config());
    const value = invert ? !disabled : disabled;
    const hasProp = 'disabled' in this.hostElement;

    if (hasProp && prop) {
      this.renderer.setProperty(this.hostElement, prop, value);
    }

    if (attr) {
      this.renderer.setAttribute(this.hostElement, attr, String(value));
    }

    if (reflectAria) {
      if (prop === 'disabled' || attr === 'aria-disabled') {
        this.renderer.setAttribute(this.hostElement, 'aria-disabled', String(value));
      }
      else if (prop?.toLowerCase() === 'readonly' || attr === 'aria-readonly') {
        this.renderer.setAttribute(this.hostElement, 'aria-readonly', String(value));
      }
    }

    this.inheritedDisabledChange.emit(value);
  }

  private normalizeConfig(cfg: Config) {
    if (typeof cfg === 'string') {
      return {
        prop: cfg || 'disabled',
        attr: undefined as string | undefined,
        reflectAria: true,
        invert: false,
      };
    }
    else return {
      prop: cfg.prop ?? 'disabled',
      attr: cfg.attr,
      reflectAria: cfg.reflectAria ?? true,
      invert: cfg.invert ?? false,
    };
  }
}
